import { useEffect, useRef } from 'react';

// ═══════════════════════════════════════════
//  RAILWAY WATERMARK — 高铁水印图片
// ═══════════════════════════════════════════

const watermarkSrc =
  'https://storage.readdy-site.link/project_files/9e4a4ca5-4366-4703-9301-bafa1f14e1f9/b628da7c-0e95-4478-bf60-07bbabedc1aa_compressed__20260624144012_729_1015.webp';

interface WatermarkPosition {
  top: string;
  left: string;
  width: string;
  rotate: string;
  opacity: number;
}

interface WatermarkSet {
  variant: 'home' | 'achievement' | 'notFound';
  positions: WatermarkPosition[];
}

const watermarkSets: WatermarkSet[] = [
  {
    variant: 'home',
    positions: [
      { top: '82%', left: '68%', width: 'min(560px, 45vw)', rotate: '-8deg', opacity: 0.10 },
      { top: '10%', left: '-3%', width: 'min(260px, 22vw)', rotate: '15deg', opacity: 0.06 },
      { top: '38%', left: '82%', width: 'min(320px, 28vw)', rotate: '5deg', opacity: 0.05 },
      { top: '55%', left: '-5%', width: 'min(200px, 18vw)', rotate: '-12deg', opacity: 0.04 },
    ],
  },
  {
    variant: 'achievement',
    positions: [
      { top: '78%', left: '72%', width: 'min(480px, 40vw)', rotate: '-8deg', opacity: 0.08 },
      { top: '15%', left: '-2%', width: 'min(220px, 20vw)', rotate: '12deg', opacity: 0.05 },
      { top: '45%', left: '88%', width: 'min(280px, 24vw)', rotate: '-5deg', opacity: 0.04 },
      { top: '90%', left: '10%', width: 'min(340px, 28vw)', rotate: '8deg', opacity: 0.04 },
      { top: '55%', left: '-4%', width: 'min(180px, 16vw)', rotate: '-15deg', opacity: 0.03 },
    ],
  },
  {
    variant: 'notFound',
    positions: [
      { top: '65%', left: '65%', width: 'min(400px, 38vw)', rotate: '-8deg', opacity: 0.08 },
      { top: '20%', left: '-4%', width: 'min(240px, 22vw)', rotate: '15deg', opacity: 0.05 },
    ],
  },
];

function RailwayWatermarkBg({ position }: { position: WatermarkPosition }) {
  return (
    <div
      className="absolute pointer-events-none select-none"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        height: 'auto',
        opacity: position.opacity,
        zIndex: 0,
        transform: `rotate(${position.rotate})`,
      }}
    >
      <img src={watermarkSrc} alt="" className="w-full h-auto object-contain" />
    </div>
  );
}

// ═══════════════════════════════════════════
//  RAILWAY TRACK — 铁路轨道水印
// ═══════════════════════════════════════════

interface RailwayTrackProps {
  y: string;
  opacity?: number;
  speed?: number;
  delay?: number;
  direction?: 'normal' | 'reverse';
}

function RailwayTrack({
  y,
  opacity = 0.035,
  speed = 10,
  delay = 0,
  direction = 'normal',
}: RailwayTrackProps) {
  const trackRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={trackRef}
      style={{
        position: 'absolute',
        top: y,
        left: 0,
        right: 0,
        height: '10px',
        opacity,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* ━━ 上钢轨 ━━ */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: '#000000',
        }}
      />
      {/* ━━ 下钢轨 ━━ */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: '#000000',
        }}
      />
      {/* ━━ 枕木 — 红/黑交替 ━━ */}
      <div
        style={{
          position: 'absolute',
          top: 1,
          left: 0,
          right: 0,
          bottom: 1,
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 28px, rgba(218,41,28,0.45) 28px, rgba(218,41,28,0.45) 30px, transparent 30px, transparent 58px, rgba(0,0,0,0.3) 58px, rgba(0,0,0,0.3) 60px, transparent 60px)',
          backgroundSize: '88px 8px',
          animation: `railway-scroll ${speed}s linear ${delay}s infinite`,
          animationDirection: direction,
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════
//  RAILWAY TIE PATTERN — 枕木纹饰底图
// ═══════════════════════════════════════════

interface TiePatternProps {
  top: string;
  height: string;
  opacity: number;
}

function RailwayTieStrip({ top, height, opacity }: TiePatternProps) {
  return (
    <div
      className="absolute left-0 right-0 pointer-events-none overflow-hidden"
      style={{ top, height, opacity, zIndex: 0 }}
    >
      {/* 枕木横条纹 */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, transparent, transparent 28px, #DA291C 28px, #DA291C 30px, transparent 30px, transparent 60px)',
          backgroundSize: '60px 100%',
        }}
      />
      {/* 两条钢轨线 */}
      <div className="absolute top-[3px] left-0 right-0 h-[1px] bg-[#DA291C]/12" />
      <div className="absolute top-[6px] left-0 right-0 h-[1px] bg-[#DA291C]/12" />
    </div>
  );
}

// ═══════════════════════════════════════════
//  DECORATION CONFIGURATION
// ═══════════════════════════════════════════

const DEGRADE_LOW = 0.06;
const DEGRADE_MED = 0.10;
const DEGRADE_HIGH = 0.14;

interface DecorationProps {
  type: 'dot' | 'ring' | 'square' | 'cross' | 'lineH' | 'lineV' | 'triangle' | 'doubleDot' | 'dash';
  x: string;
  y: string;
  size: number;
  color: 'red' | 'black';
  opacity: number;
  rotate?: number;
  float?: 'up' | 'down' | 'left' | 'right';
  floatDist?: number;
  floatDuration?: number;
  delay?: number;
}

const homeDecorations: DecorationProps[] = [
  // HeroBanner 下方空白区域
  { type: 'dot', x: '8%', y: '88%', size: 4, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 8, floatDuration: 5, delay: 0 },
  { type: 'dot', x: '92%', y: '85%', size: 3, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 6, floatDuration: 4, delay: 1 },
  { type: 'ring', x: '15%', y: '80%', size: 8, color: 'red', opacity: DEGRADE_MED, rotate: 0, float: 'up', floatDist: 10, floatDuration: 6, delay: 0.5 },
  { type: 'cross', x: '85%', y: '78%', size: 6, color: 'black', opacity: DEGRADE_MED, float: 'down', floatDist: 8, floatDuration: 5.5, delay: 1.5 },
  { type: 'lineV', x: '5%', y: '82%', size: 20, color: 'red', opacity: DEGRADE_LOW, rotate: 15, float: 'up', floatDist: 5, floatDuration: 7, delay: 2 },
  { type: 'lineH', x: '95%', y: '90%', size: 20, color: 'black', opacity: DEGRADE_LOW, rotate: -10, float: 'left', floatDist: 6, floatDuration: 6, delay: 0.8 },

  // Showcase 区域上方
  { type: 'square', x: '3%', y: '105%', size: 5, color: 'black', opacity: DEGRADE_MED, rotate: 45, float: 'down', floatDist: 7, floatDuration: 5, delay: 0.3 },
  { type: 'doubleDot', x: '97%', y: '108%', size: 5, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 5, floatDuration: 4.5, delay: 1.2 },
  { type: 'triangle', x: '10%', y: '115%', size: 6, color: 'red', opacity: DEGRADE_LOW, float: 'down', floatDist: 9, floatDuration: 6.5, delay: 0.7 },

  // QuickNav 区域
  { type: 'dot', x: '6%', y: '145%', size: 3, color: 'black', opacity: DEGRADE_LOW, float: 'up', floatDist: 6, floatDuration: 5, delay: 0.2 },
  { type: 'ring', x: '94%', y: '150%', size: 7, color: 'red', opacity: DEGRADE_MED, float: 'down', floatDist: 8, floatDuration: 6, delay: 1.5 },
  { type: 'dash', x: '12%', y: '155%', size: 12, color: 'red', opacity: DEGRADE_LOW, rotate: 30, float: 'left', floatDist: 5, floatDuration: 4.5, delay: 0.9 },
  { type: 'dash', x: '88%', y: '148%', size: 10, color: 'black', opacity: DEGRADE_LOW, rotate: -20, float: 'right', floatDist: 6, floatDuration: 5.5, delay: 2.2 },
  { type: 'cross', x: '4%', y: '160%', size: 5, color: 'red', opacity: DEGRADE_MED, float: 'up', floatDist: 7, floatDuration: 5, delay: 1.8 },

  // Footer 上方
  { type: 'dot', x: '18%', y: '185%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'down', floatDist: 5, floatDuration: 4, delay: 0.4 },
  { type: 'dot', x: '82%', y: '182%', size: 4, color: 'black', opacity: DEGRADE_LOW, float: 'up', floatDist: 6, floatDuration: 5, delay: 1.1 },
  { type: 'square', x: '7%', y: '190%', size: 4, color: 'red', opacity: DEGRADE_MED, rotate: 30, float: 'left', floatDist: 4, floatDuration: 6, delay: 0.6 },
];

const achievementDecorations: DecorationProps[] = [
  // 顶部导航区域
  { type: 'dot', x: '3%', y: '6%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 5, floatDuration: 4, delay: 0 },
  { type: 'dot', x: '97%', y: '4%', size: 4, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 6, floatDuration: 5, delay: 0.8 },
  { type: 'ring', x: '8%', y: '8%', size: 6, color: 'red', opacity: DEGRADE_MED, float: 'up', floatDist: 7, floatDuration: 5.5, delay: 1.2 },
  { type: 'cross', x: '92%', y: '10%', size: 5, color: 'black', opacity: DEGRADE_MED, float: 'down', floatDist: 5, floatDuration: 4.5, delay: 0.5 },

  // 面包屑区域
  { type: 'dot', x: '95%', y: '15%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 4, floatDuration: 5, delay: 0.3 },
  { type: 'lineH', x: '5%', y: '12%', size: 16, color: 'black', opacity: DEGRADE_LOW, rotate: 12, float: 'left', floatDist: 4, floatDuration: 6, delay: 1.5 },

  // 内容区左侧空白
  { type: 'dot', x: '2%', y: '30%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 5, floatDuration: 4, delay: 0.7 },
  { type: 'square', x: '4%', y: '45%', size: 5, color: 'black', opacity: DEGRADE_MED, rotate: 45, float: 'down', floatDist: 6, floatDuration: 5.5, delay: 1.8 },
  { type: 'doubleDot', x: '3%', y: '60%', size: 4, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 5, floatDuration: 5, delay: 0.9 },
  { type: 'triangle', x: '5%', y: '75%', size: 5, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 7, floatDuration: 6, delay: 1.3 },
  { type: 'dash', x: '2%', y: '85%', size: 10, color: 'red', opacity: DEGRADE_LOW, rotate: 25, float: 'left', floatDist: 4, floatDuration: 5, delay: 0.4 },

  // 内容区右侧空白
  { type: 'dot', x: '98%', y: '35%', size: 4, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 6, floatDuration: 4.5, delay: 1.0 },
  { type: 'ring', x: '96%', y: '50%', size: 7, color: 'red', opacity: DEGRADE_MED, float: 'up', floatDist: 8, floatDuration: 6, delay: 0.6 },
  { type: 'cross', x: '97%', y: '65%', size: 5, color: 'black', opacity: DEGRADE_MED, float: 'down', floatDist: 5, floatDuration: 5, delay: 1.7 },
  { type: 'lineV', x: '99%', y: '80%', size: 18, color: 'red', opacity: DEGRADE_LOW, rotate: -8, float: 'up', floatDist: 4, floatDuration: 5.5, delay: 2.1 },

  // 底部
  { type: 'dot', x: '10%', y: '95%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 4, floatDuration: 4, delay: 0.2 },
  { type: 'dot', x: '90%', y: '92%', size: 3, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 5, floatDuration: 5, delay: 1.4 },
];

const notFoundDecorations: DecorationProps[] = [
  { type: 'dot', x: '10%', y: '20%', size: 4, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 6, floatDuration: 5, delay: 0 },
  { type: 'dot', x: '85%', y: '15%', size: 3, color: 'black', opacity: DEGRADE_LOW, float: 'down', floatDist: 5, floatDuration: 4, delay: 0.8 },
  { type: 'ring', x: '20%', y: '75%', size: 8, color: 'red', opacity: DEGRADE_MED, float: 'up', floatDist: 7, floatDuration: 6, delay: 1.2 },
  { type: 'cross', x: '75%', y: '80%', size: 6, color: 'black', opacity: DEGRADE_MED, float: 'down', floatDist: 6, floatDuration: 5, delay: 0.5 },
  { type: 'square', x: '15%', y: '35%', size: 5, color: 'red', opacity: DEGRADE_MED, rotate: 30, float: 'left', floatDist: 4, floatDuration: 5, delay: 0.3 },
  { type: 'doubleDot', x: '80%', y: '40%', size: 4, color: 'black', opacity: DEGRADE_LOW, float: 'right', floatDist: 5, floatDuration: 4.5, delay: 1.5 },
  { type: 'triangle', x: '8%', y: '55%', size: 5, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 6, floatDuration: 5.5, delay: 0.9 },
  { type: 'dash', x: '88%', y: '60%', size: 12, color: 'red', opacity: DEGRADE_LOW, rotate: -20, float: 'down', floatDist: 4, floatDuration: 6, delay: 1.8 },
  { type: 'dot', x: '50%', y: '10%', size: 3, color: 'black', opacity: DEGRADE_LOW, float: 'up', floatDist: 5, floatDuration: 4, delay: 0.6 },
  { type: 'lineV', x: '5%', y: '50%', size: 16, color: 'red', opacity: DEGRADE_LOW, rotate: 10, float: 'down', floatDist: 5, floatDuration: 5, delay: 1.1 },
  { type: 'lineH', x: '92%', y: '30%', size: 14, color: 'black', opacity: DEGRADE_LOW, rotate: -8, float: 'left', floatDist: 6, floatDuration: 5.5, delay: 2.0 },
  { type: 'dot', x: '45%', y: '90%', size: 3, color: 'red', opacity: DEGRADE_LOW, float: 'up', floatDist: 4, floatDuration: 4, delay: 1.3 },
];

// ── 枕木纹饰配置 ──

interface TieStripConfig {
  top: string;
  height: string;
  opacity: number;
}

const homeTieStrips: TieStripConfig[] = [
  { top: '75%', height: '10%', opacity: 0.03 },
  { top: '125%', height: '8%', opacity: 0.025 },
  { top: '170%', height: '6%', opacity: 0.02 },
];

const achievementTieStrips: TieStripConfig[] = [
  { top: '18%', height: '8%', opacity: 0.035 },
  { top: '40%', height: '12%', opacity: 0.028 },
  { top: '62%', height: '10%', opacity: 0.03 },
  { top: '85%', height: '6%', opacity: 0.022 },
];

const notFoundTieStrips: TieStripConfig[] = [
  { top: '30%', height: '8%', opacity: 0.03 },
  { top: '55%', height: '10%', opacity: 0.025 },
];

// ═══════════════════════════════════════════
//  GEOMETRIC DECORATION RENDERERS
// ═══════════════════════════════════════════

function getColorHex(color: 'red' | 'black'): string {
  return color === 'red' ? '#DA291C' : '#000000';
}

function DecorationElement({ item }: { item: DecorationProps }) {
  const elRef = useRef<HTMLDivElement>(null);
  const color = getColorHex(item.color);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !item.float) return;
    const dist = item.floatDist || 6;
    const dur = item.floatDuration || 5;
    const delay = item.delay || 0;
    let raf: number;
    const startTime = performance.now() + delay * 1000;

    const animate = (now: number) => {
      const t = Math.max(0, now - startTime);
      const progress = (t / 1000 / dur) % 1;
      const offset = Math.sin(progress * Math.PI * 2) * dist;
      let tx = 0;
      let ty = 0;
      switch (item.float) {
        case 'up': ty = -offset; break;
        case 'down': ty = offset; break;
        case 'left': tx = -offset; break;
        case 'right': tx = offset; break;
      }
      el.style.transform = `translate(${tx}px, ${ty}px)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [item.float, item.floatDist, item.floatDuration, item.delay]);

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: item.x,
    top: item.y,
    opacity: item.opacity,
    pointerEvents: 'none',
  };

  if (item.rotate) {
    baseStyle.transform = `rotate(${item.rotate}deg)`;
  }

  switch (item.type) {
    case 'dot':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: item.size,
            height: item.size,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      );

    case 'ring':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: item.size,
            height: item.size,
            borderRadius: '50%',
            border: `1.5px solid ${color}`,
            backgroundColor: 'transparent',
          }}
        />
      );

    case 'square':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: item.size,
            height: item.size,
            backgroundColor: color,
            borderRadius: '1px',
          }}
        />
      );

    case 'cross': {
      const s = item.size;
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: s,
            height: s,
            position: 'absolute',
          }}
        >
          <div style={{ position: 'absolute', left: (s - 1) / 2, top: 0, width: 1, height: s, backgroundColor: color }} />
          <div style={{ position: 'absolute', left: 0, top: (s - 1) / 2, width: s, height: 1, backgroundColor: color }} />
        </div>
      );
    }

    case 'lineH':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: item.size,
            height: 1,
            backgroundColor: color,
            borderRadius: '0.5px',
          }}
        />
      );

    case 'lineV':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: 1,
            height: item.size,
            backgroundColor: color,
            borderRadius: '0.5px',
          }}
        />
      );

    case 'triangle': {
      const s = item.size;
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: 0,
            height: 0,
            borderLeft: `${s / 2}px solid transparent`,
            borderRight: `${s / 2}px solid transparent`,
            borderBottom: `${s}px solid ${color}`,
          }}
        />
      );
    }

    case 'doubleDot': {
      const s = item.size;
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: s * 2,
            height: s,
            display: 'flex',
            gap: 2,
          }}
        >
          <div style={{ width: s / 2, height: s / 2, borderRadius: '50%', backgroundColor: color }} />
          <div style={{ width: s / 2, height: s / 2, borderRadius: '50%', backgroundColor: color }} />
        </div>
      );
    }

    case 'dash':
      return (
        <div
          ref={elRef}
          style={{
            ...baseStyle,
            width: item.size,
            height: 1.5,
            backgroundColor: color,
            borderRadius: '0.5px',
          }}
        />
      );

    default:
      return null;
  }
}

interface BackgroundDecorationsProps {
  variant: 'home' | 'achievement' | 'notFound';
}

const homeTracks: RailwayTrackProps[] = [
  { y: '92%', opacity: 0.03, speed: 12, delay: 0, direction: 'normal' },
  { y: '142%', opacity: 0.025, speed: 15, delay: 2, direction: 'reverse' },
  { y: '178%', opacity: 0.03, speed: 10, delay: 1, direction: 'normal' },
];

const achievementTracks: RailwayTrackProps[] = [
  { y: '50%', opacity: 0.03, speed: 14, delay: 0, direction: 'reverse' },
  { y: '88%', opacity: 0.025, speed: 11, delay: 1.5, direction: 'normal' },
];

const notFoundTracks: RailwayTrackProps[] = [
  { y: '35%', opacity: 0.03, speed: 10, delay: 0, direction: 'normal' },
  { y: '65%', opacity: 0.03, speed: 14, delay: 1, direction: 'reverse' },
];

export default function BackgroundDecorations({ variant }: BackgroundDecorationsProps) {
  const items = variant === 'home'
    ? homeDecorations
    : variant === 'achievement'
      ? achievementDecorations
      : notFoundDecorations;

  const tracks = variant === 'home'
    ? homeTracks
    : variant === 'achievement'
      ? achievementTracks
      : notFoundTracks;

  const watermarkPositions = watermarkSets.find((s) => s.variant === variant)?.positions || [];

  const tieStrips = variant === 'home'
    ? homeTieStrips
    : variant === 'achievement'
      ? achievementTieStrips
      : notFoundTieStrips;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {/* ═══ 枕木纹饰底图 — 多段横向枕木条纹 ═══ */}
      {tieStrips.map((strip, index) => (
        <RailwayTieStrip
          key={`tie-${variant}-${index}`}
          top={strip.top}
          height={strip.height}
          opacity={strip.opacity}
        />
      ))}

      {/* ═══ 多位置高铁水印底图 — absolute 定位，随页面滚动 ═══ */}
      {watermarkPositions.map((pos, index) => (
        <RailwayWatermarkBg key={`watermark-${variant}-${index}`} position={pos} />
      ))}

      {/* ═══ 铁路轨道装饰 ═══ */}
      {tracks.map((track, index) => (
        <RailwayTrack key={`track-${variant}-${index}`} {...track} />
      ))}

      {/* ═══ 几何装饰点 ═══ */}
      {items.map((item, index) => (
        <DecorationElement key={`${variant}-${index}`} item={item} />
      ))}
    </div>
  );
}